import React from 'react';
import { Box, Typography } from '@mui/material';
import WorkItemEmailViewer from './WorkItemEmailViewer';
import WorkItemNextStepCard from './WorkItemNextStepCard';
import DrawerDocumentFacts from './DrawerDocumentFacts';
import { DrawerFacetTriageProps } from './DrawerFacetTypes';
import { getWorkItemSourceInfo, linkedEmailMessageId } from '../utils/workItemDisplay';
import { formatHumanDrawerDate } from '../utils/deriveNextStep';
import { buildDocumentFacts } from '../utils/drawerDocumentFacts';

type OverviewInput = Pick<DrawerFacetTriageProps, 'item' | 'draft' | 'nextStep' | 'stakes' | 'invoiceData' | 'extractionFields' | 'hideReadSummary'>;

/** What Overview shows. `isEmpty` is true when it would render nothing at all. */
export function buildOverviewModel({
    item, draft, nextStep = null, stakes = [], invoiceData, extractionFields, hideReadSummary = false,
}: OverviewInput) {
    const meta = (draft.meta || item?.meta || {}) as Record<string, unknown>;
    const showAiDegraded = (meta.ai_availability as { status?: string } | undefined)?.status === 'degraded';
    const sourceInfo = item ? getWorkItemSourceInfo(item) : null;
    const linkedId = linkedEmailMessageId(item);
    const showOriginalEmail = Boolean(linkedId) || sourceInfo?.sourceType === 'email';
    // A copied mail envelope is not a summary; the complete message remains available below.
    const summary = /^\s*(?:-+\s*(?:forwarded|original) message|begin forwarded message:|from:|sent from my\b)/i.test(draft.summary || '')
        ? null : draft.summary;
    const notes = /^\s*from:[^\n]+$/i.test(draft.notes || '') ? null : draft.notes;
    const eventDate = formatHumanDrawerDate(item?.event_date);
    const dueDate = formatHumanDrawerDate(item?.due_date);
    const documentFacts = buildDocumentFacts({ title: item?.title, invoiceData, extractionFields });
    const showNextStep = Boolean(nextStep) && nextStep?.source !== 'scam_risk';
    const stakeIds = new Set(stakes.map((stake) => stake.id));
    const documentDue = documentFacts.find((fact) => fact.key === 'due')?.value;
    const showDue = Boolean(dueDate) && dueDate !== documentDue
        && !(showNextStep && stakeIds.has('due'));
    const showEvent = Boolean(eventDate) && !(showNextStep && stakeIds.has('event'));
    const showSummary = Boolean(summary) && !hideReadSummary;
    const showSummaryUnavailable = showAiDegraded && !summary;
    const isEmpty = !showNextStep && !showSummary && !showSummaryUnavailable && documentFacts.length === 0
        && !showDue && !showEvent && !showOriginalEmail && !notes;
    return {
        linkedId, summary, notes, eventDate, dueDate, showNextStep, showDue, showEvent,
        showSummary, showSummaryUnavailable, showOriginalEmail, isEmpty,
    };
}

/** Read the situation first. Editing, classification and provenance are optional. */
const DrawerFacetTriage: React.FC<DrawerFacetTriageProps> = ({
    item, draft, scamRisk, nextStep = null, stakes = [],
    onNextStepCta, onEmailExpandedChange, emailViewerExpanded = false,
    onMailboxGmailAuthIssue, onEmailSafetyChange, invoiceData, extractionFields,
    hideReadSummary = false,
}) => {
    const {
        linkedId, summary, notes, eventDate, dueDate, showNextStep, showDue, showEvent,
        showSummary, showSummaryUnavailable, showOriginalEmail,
    } = buildOverviewModel({ item, draft, nextStep, stakes, invoiceData, extractionFields, hideReadSummary });

    return (
        <Box sx={{ display: 'grid', gap: 2.5 }}>
            {showNextStep && (
                <WorkItemNextStepCard nextStep={nextStep} stakes={stakes} onCta={onNextStepCta} />
            )}
            {showSummary && (
                <Box>
                    <Typography variant="body2" sx={{ color: 'var(--color-text-primary)', lineHeight: 1.65, whiteSpace: 'pre-line' }}>
                        {summary}
                    </Typography>
                </Box>
            )}
            {showSummaryUnavailable && (
                <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                    A summary is unavailable. Check the original message for details.
                </Typography>
            )}
            <DrawerDocumentFacts title={item?.title} invoiceData={invoiceData} extractionFields={extractionFields} />
            {(showDue || showEvent) && (
                <Box sx={{ display: 'grid', gap: 0.5 }}>
                    {showDue && <Typography variant="body2">Task due date: {dueDate}</Typography>}
                    {showEvent && <Typography variant="body2">{item?.event_type?.replace(/_/g, ' ') || 'Event'}: {eventDate}</Typography>}
                </Box>
            )}
            {showOriginalEmail && (
                <Box data-testid="WB_TRIAGE_ORIGINAL_EMAIL">
                    {item && linkedId ? (
                        <WorkItemEmailViewer
                            workItemId={item.id} gmailMessageId={linkedId}
                            autoExpand={emailViewerExpanded} embedded
                            allowReply={scamRisk?.level !== 'high'}
                            safetyKey={item.safety?.revision || JSON.stringify(item.safety ?? null)}
                            onExpandedChange={onEmailExpandedChange}
                            onMailboxGmailAuthIssue={onMailboxGmailAuthIssue}
                            onSafetyChange={onEmailSafetyChange}
                        />
                    ) : (
                        <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                            The original email is unavailable.
                        </Typography>
                    )}
                </Box>
            )}
            {notes && (
                <Box>
                    <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>Notes</Typography>
                    <Typography variant="body2" sx={{ color: 'var(--color-text-primary)', whiteSpace: 'pre-wrap' }}>{notes}</Typography>
                </Box>
            )}
        </Box>
    );
};

export default DrawerFacetTriage;
